import { createContext, PropsWithChildren, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { Notification, NotificationListResponse, NotificationType, NotificationStatus } from '../shared/types/Notification';
import { notificationService } from '../services/notification.service';
import { signalRService } from '../services/signalr.service';
import { useAuth } from '../shared/hooks/useAuth';
import { useServiceStatus } from './service-status';

export type NotificationContextType = {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  hasMore: boolean;
  isServiceAvailable: boolean;
  fetchNotifications: () => Promise<void>;
  loadMore: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  refreshUnreadCount: () => Promise<void>;
};

const NotificationContext = createContext<NotificationContextType>({} as NotificationContextType);

const NOTIFICATIONS_PER_PAGE = 10;
const REFRESH_INTERVAL = 60000;

export function NotificationProvider({ children }: PropsWithChildren) {
  const { user } = useAuth();
  const { isServiceOnline, status } = useServiceStatus();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);

  // Track service availability
  const isServiceAvailable = isServiceOnline('notification');
  const wasServiceAvailableRef = useRef<boolean>(isServiceAvailable);

  /**
   * Verifica se o usuário deve receber a notificação
   * NOTA: A API já filtra notificações por usuário, então aqui apenas validamos
   * que o userId corresponde ou confiamos na API
   */
  const shouldUserReceiveNotification = useCallback((notification: any) => {
    if (!user) {
      return false;
    }

    const userId = user.id;
    const notificationUserId = notification.userId || notification.UserId;

    // Se a notificação tem um userId específico, verificar se é para este usuário
    if (notificationUserId) {
      return notificationUserId === userId;
    }

    // Se não tem userId específico, verificar no data
    let notificationData = null;
    try {
      const dataStr = notification.Data || notification.data;
      if (dataStr) {
        notificationData = typeof dataStr === 'string' ? JSON.parse(dataStr) : dataStr;
      }
    } catch {
      // Se der erro ao parsear, aceitar a notificação
    }

    // Verificar se algum campo no data contém o userId do usuário
    if (notificationData) {
      const possibleUserFields = [
        'NewInspectorUserId',
        'PreviousInspectorUserId',
        'ProviderManagerUserId',
        'BankUserId',
        'inspectorId',
        'providerId',
      ];

      for (const field of possibleUserFields) {
        if (notificationData[field] === userId) {
          return true;
        }
      }
    }

    // Se não tem userId e não encontrou no data, aceitar (confiar na API)
    // A API já faz o filtro por usuário
    return true;
  }, [user]);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;

    // Don't try to fetch if service is offline
    if (!isServiceAvailable) {
      return;
    }

    try {
      setLoading(true);
      const response: NotificationListResponse = await notificationService.getNotifications(1, NOTIFICATIONS_PER_PAGE);

      const filteredNotifications = response.notifications.filter(notification => {
        return shouldUserReceiveNotification(notification);
      });

      setNotifications(filteredNotifications);
      setUnreadCount(response.unreadCount);
      setPage(1);
      setHasMore(filteredNotifications.length < response.total);
    } catch {
    } finally {
      setLoading(false);
    }
  }, [user, shouldUserReceiveNotification, isServiceAvailable]);

  const loadMore = useCallback(async () => {
    if (!user || !hasMore || loading) return;

    // Don't try to load more if service is offline
    if (!isServiceAvailable) {
      return;
    }

    try {
      setLoading(true);
      const nextPage = page + 1;
      const response: NotificationListResponse = await notificationService.getNotifications(
        nextPage,
        NOTIFICATIONS_PER_PAGE
      );

      const filteredNotifications = response.notifications.filter(notification => {
        return shouldUserReceiveNotification(notification);
      });

      setNotifications((prev) => [...prev, ...filteredNotifications]);
      setPage(nextPage);
      setHasMore(notifications.length + filteredNotifications.length < response.total);
    } catch {
    } finally {
      setLoading(false);
    }
  }, [user, hasMore, loading, page, notifications.length, shouldUserReceiveNotification, isServiceAvailable]);

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await notificationService.markAsRead(notificationId);

      setNotifications((prev) =>
        prev.map((notification) =>
          notification.id === notificationId
            ? { ...notification, status: NotificationStatus.READ }
            : notification
        )
      );

      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error: any) {
      throw error;
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await notificationService.markAllAsRead();

      setNotifications((prev) =>
        prev.map((notification) => ({ ...notification, status: NotificationStatus.READ }))
      );

      setUnreadCount(0);
    } catch (error: any) {
      throw error;
    }
  }, []);

  const deleteNotification = useCallback(async (notificationId: string) => {
    try {
      await notificationService.deleteNotification(notificationId);

      setNotifications((prev) => {
        const notification = prev.find((n) => n.id === notificationId);
        if (notification && notification.status === NotificationStatus.UNREAD) {
          setUnreadCount((count) => Math.max(0, count - 1));
        }
        return prev.filter((n) => n.id !== notificationId);
      });
    } catch (error: any) {
      throw error;
    }
  }, []);

  const refreshUnreadCount = useCallback(async () => {
    if (!user) return;

    // Don't try to refresh if service is offline
    if (!isServiceAvailable) {
      return;
    }

    try {
      const count = await notificationService.getUnreadCount();
      setUnreadCount(count);
    } catch {
    }
  }, [user, isServiceAvailable]);

  /**
   * Converte string de data para Date, garantindo que seja interpretada como UTC
   * Backend salva em UTC mas pode serializar sem o sufixo 'Z'
   */
  const parseUtcDate = useCallback((dateValue: string | Date | undefined | null): Date => {
    if (!dateValue) {
      return new Date();
    }

    // Se já é um Date, retornar
    if (dateValue instanceof Date) {
      return dateValue;
    }

    const dateString = String(dateValue);

    // Se a string já termina com Z ou tem timezone (+/-), usar diretamente
    if (dateString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateString)) {
      return new Date(dateString);
    }

    // Caso contrário, adicionar 'Z' para indicar que é UTC
    return new Date(dateString + 'Z');
  }, []);

  const handleNewNotification = useCallback((notification: any) => {
    try {
      const shouldReceive = shouldUserReceiveNotification(notification);

      if (!shouldReceive) {
        return;
      }

      const newNotification: Notification = {
        id: notification.Id || notification.id,
        userId: notification.UserId || notification.userId,
        type: (notification.Type || notification.type) as NotificationType,
        title: notification.Title || notification.title || '',
        message: notification.Message || notification.message || '',
        status: NotificationStatus.UNREAD,
        createdAt: parseUtcDate(notification.CreatedAt || notification.createdAt),
        priority: notification.Priority || notification.priority,
        category: notification.Category || notification.category,
        data: notification.Data || notification.data,
      };

      setNotifications((prev) => [newNotification, ...prev]);

      setUnreadCount((prev) => prev + 1);
    } catch {
    }
  }, [shouldUserReceiveNotification, parseUtcDate]);

  useEffect(() => {
    if (user) {
      fetchNotifications();
    } else {
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [user, fetchNotifications]);

  useEffect(() => {
    if (!user) {
      signalRService.disconnect();
      return;
    }

    // Don't try to connect if service is offline
    if (!isServiceAvailable) {
      signalRService.disconnect();
      return;
    }

    const connectSignalR = async () => {
      try {
        await signalRService.connect();
        signalRService.onNotificationReceived(handleNewNotification);
      } catch {
      }
    };

    connectSignalR();

    return () => {
      signalRService.offNotificationReceived();
    };
  }, [user, handleNewNotification, isServiceAvailable]);

  // Refetch notifications when service comes back online
  useEffect(() => {
    if (isServiceAvailable && !wasServiceAvailableRef.current && user) {
      // Service just came back online, refetch notifications
      fetchNotifications();
    }
    wasServiceAvailableRef.current = isServiceAvailable;
  }, [isServiceAvailable, user, fetchNotifications]);

  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      refreshUnreadCount();
    }, REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [user, refreshUnreadCount]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        hasMore,
        isServiceAvailable,
        fetchNotifications,
        loadMore,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        refreshUnreadCount,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotificationContext() {
  return useContext(NotificationContext);
}
